// These atoms persist their state across page reloads.
//
// There are two types:
// - local storage (shared across tabs), used for things like user authentication.
// - session storage (not shared across tabs), used for things like remembering applied visit filters.
//
import { DURABLE_FILTER_SYNC_RECORDS_STORAGE_KEY } from '@/state/durableFilters/durableFilterSyncRecords';
import { appStore } from '@/utils/jotaiStore';
import * as Sentry from '@sentry/react';
import { WritableAtom, atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage';
import moment, { Moment } from 'moment-timezone';
import { ZodSchema, z } from 'zod';

//////////////////////////////////////////////////////////////////////////////
// region Private helpers.
//////////////////////////////////////////////////////////////////////////////

// This lets us sync Jotai atoms to browser storage.
type JotaiStorage = SyncStorage<string>;
const jotaiStorageFromBrowserStorage = (storage: Storage): JotaiStorage => {
    return {
        getItem: (key, initialValue) => storage.getItem(key) ?? initialValue,
        setItem: (key, value) => storage.setItem(key, value),
        removeItem: (key) => storage.removeItem(key),
        subscribe: (key, callback, initialValue) => {
            const listener = (event: StorageEvent) => {
                if (event.storageArea === storage && event.key === key) {
                    callback(event.newValue ?? initialValue);
                }
            };
            window.addEventListener('storage', listener);
            return () => window.removeEventListener('storage', listener);
        },
    };
};
const jotaiLocalStorage = jotaiStorageFromBrowserStorage(window.localStorage);
const jotaiSessionStorage = jotaiStorageFromBrowserStorage(window.sessionStorage);

// We assume the Zod schema does not have transformations, and the input and
//  output types are the same. Otherwise, the code in this file will break.
type ValueFor<Schema extends ZodSchema> = z.infer<Schema>;
type AtomFor<Value> = WritableAtom<Value, [Value], void>;

const atomWithStorageJSONLike = <Schema extends ZodSchema, Value = ValueFor<Schema>>(
    key: string,
    initialValue: Value,
    schema: Schema,
    storage: JotaiStorage,
) => {
    // We disable the following rule, as this wrapper is allowed to call `atomWithStorage` directly.
    // eslint-disable-next-line no-restricted-syntax
    const rawAtom = atomWithStorage<string>(key, JSON.stringify(initialValue), storage, {
        getOnInit: true,
    });

    return atom(
        (get) => {
            const rawValue = get(rawAtom);
            try {
                return schema.parse(JSON.parse(rawValue)) as Value;
            } catch {
                const errorMessage = `Invalid value in atom storage for key '${key}': ${rawValue.toString()}`;
                Sentry.captureException(new Error(errorMessage));

                appStore.set(rawAtom, JSON.stringify(initialValue));
                return initialValue;
            }
        },
        (_get, set, value) => set(rawAtom, JSON.stringify(value)),
    );
};

const atomWithStorageNonJSONLike = <Value, JSONLikeSchema extends ZodSchema>(
    key: string,
    initialValue: Value,
    jsonLikeSchema: JSONLikeSchema,
    storage: JotaiStorage,
    transformToJsonLike: (value: Value) => ValueFor<JSONLikeSchema>,
    untransformFromJsonLike: (value: ValueFor<JSONLikeSchema>) => Value,
): AtomFor<Value> => {
    const jsonLikeAtom: AtomFor<ValueFor<JSONLikeSchema>> = atomWithStorageJSONLike(
        key,
        transformToJsonLike(initialValue),
        jsonLikeSchema,
        storage,
    );

    return atom(
        (get) => untransformFromJsonLike(get(jsonLikeAtom)),
        (_get, set, value) => set(jsonLikeAtom, transformToJsonLike(value)),
    );
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Persistent atoms registry.
//////////////////////////////////////////////////////////////////////////////

// Registry to track which localStorage keys should persist across logout.
// These atoms contain non-sensitive UX preferences that should be preserved.
export const PERSISTENT_LOCAL_STORAGE_KEYS = new Set<string>([
    'globalNavigationDrawerOpen',
    'enabledContainersByVariation',
    'featureFlagOverrideKey', // Unlock passphrase for the non-prod flag override tools
]);

// Registry to track which sessionStorage keys should persist across logout.
// These are only useful once the session that wrote them has ended, so they have to
//  outlive it.
export const PERSISTENT_SESSION_STORAGE_KEYS = new Set<string>([
    'lastAuthedPage', // Page to return to after signing back in, scoped to the user's UUID
]);

// Registry of localStorage keys the app no longer reads. Each one is now a
// sessionStorage key of the same name, so a value left under the old one is
// carried over before the key is dropped.
const RETIRED_LOCAL_STORAGE_KEYS = new Set<string>([
    'selectedTagsByOrg',
    'selectedPatientTagsByOrg',
    DURABLE_FILTER_SYNC_RECORDS_STORAGE_KEY,
]);

/**
 * Moves whatever a retired localStorage key still holds into sessionStorage, then
 * drops the key. A key sessionStorage already holds is left alone, so a value in
 * use wins over the one left behind.
 */
export const migrateRetiredLocalStorageKeys = () => {
    RETIRED_LOCAL_STORAGE_KEYS.forEach((key) => {
        try {
            const retired = window.localStorage.getItem(key);
            if (retired !== null && window.sessionStorage.getItem(key) === null) {
                window.sessionStorage.setItem(key, retired);
            }
            window.localStorage.removeItem(key);
        } catch (error) {
            Sentry.captureException(error);
        }
    });
};

// endregion
//////////////////////////////////////////////////////////////////////////////
// region Public interface.
//////////////////////////////////////////////////////////////////////////////

/**
 * Creates a Jotai atom that persists its value to localStorage.
 *
 * @param key - The localStorage key to store the atom value under
 * @param initialValue - The default value if nothing is stored
 * @param schema - Zod schema for validation and type safety
 */
export const atomWithLocalStorage = <Schema extends ZodSchema>(
    key: string,
    initialValue: ValueFor<Schema>,
    schema: Schema,
): AtomFor<ValueFor<Schema>> => {
    return atomWithStorageJSONLike(key, initialValue, schema, jotaiLocalStorage);
};

export const atomWithSessionStorage = <Schema extends ZodSchema>(
    key: string,
    initialValue: ValueFor<Schema>,
    schema: Schema,
): AtomFor<ValueFor<Schema>> => {
    return atomWithStorageJSONLike(key, initialValue, schema, jotaiSessionStorage);
};

export const momentAtomWithSessionStorage = (key: string, initialValue: Moment) => {
    return atomWithStorageNonJSONLike(
        key,
        initialValue,
        z.string(),
        jotaiSessionStorage,
        (valueAsMoment) => valueAsMoment.toJSON(),
        (valueAsString) => moment(valueAsString),
    );
};

export const optionalMomentAtomWithSessionStorage = (key: string, initialValue: Moment | null) => {
    return atomWithStorageNonJSONLike(
        key,
        initialValue,
        z.string().nullable(),
        jotaiSessionStorage,
        (valueAsMoment) => valueAsMoment?.toJSON() ?? null,
        (valueAsString) => (valueAsString ? moment(valueAsString) : null),
    );
};

// endregion
